import { orderV1ZodSchema } from "@@/shared/orders/order";
import { userIdZodSchema } from "@@/shared/users/user-id";
import type { Except } from "type-fest";
import { z } from "zod";
import type { _OrderEventBaseFactoryParams } from "./_order-base.event";
import {
    _orderEventBaseFactory,
    _orderEventBaseZodObject,
} from "./_order-base.event";

import { anonymizedContactInfoFactory } from "@@/shared/locations/contact-info";
import { quoteIdZodSchema } from "@@/shared/quotes/quote-id";
import { customerIdZodSchema } from "@@/shared/users/customer-id";

const orderCreatedFromQuoteEventZodSchema = _orderEventBaseZodObject.merge(
    z.object({
        type: z.literal("CREATED_FROM_QUOTE"),
        // old orders that didn't have a CreatedFromQuote status in status history
        // might not have had a userId for CreatedFromQuoteBy
        // so if it was missing those orders might just have fallen back to
        // the customerId
        createdBy: userIdZodSchema.or(customerIdZodSchema),
        quote: quoteIdZodSchema,
        order: z.lazy(() => orderV1ZodSchema),
    }),
);

type OrderCreatedFromQuoteEvent = z.infer<
    typeof orderCreatedFromQuoteEventZodSchema
>;

type Params = Except<
    _OrderEventBaseFactoryParams &
        Except<
            OrderCreatedFromQuoteEvent,
            "type" | "_id" | "_type" | "_createdAt"
        >,
    | "orderGroupId"
    | "orderId"
    | "providerId"
    | "providerSlug"
    | "_version"
    | "signature"
> & {
    _version?: number;
};

const orderCreatedFromQuoteEventFactory = (
    params: Params,
): OrderCreatedFromQuoteEvent => ({
    ..._orderEventBaseFactory({
        orderId: params.order._id,
        orderGroupId: params.order.orderGroupId,
        _version: params._version ?? 0,
        providerId: params.order.providerId,
        providerSlug: params.order.providerSlug,
        signature: [params.order._id, "CREATED_FROM_QUOTE"],
    }),

    type: "CREATED_FROM_QUOTE",
    createdBy: params.createdBy,
    order: params.order,
    quote: params.quote,
});

const isOrderCreatedFromQuoteEvent = (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    event: any,
): event is OrderCreatedFromQuoteEvent => {
    return (
        !!event &&
        event._type === "ORDER_EVENT" &&
        event?.type === "CREATED_FROM_QUOTE"
    );
};

const anonymizeOrderCreatedFromQuoteEvent = (
    event: OrderCreatedFromQuoteEvent,
): OrderCreatedFromQuoteEvent => ({
    ...event,
    order: {
        ...event.order,
        contactInfo: anonymizedContactInfoFactory(event.order.contactInfo),
    },
});

export {
    anonymizeOrderCreatedFromQuoteEvent,
    isOrderCreatedFromQuoteEvent,
    orderCreatedFromQuoteEventFactory,
    orderCreatedFromQuoteEventZodSchema,
};
export type { OrderCreatedFromQuoteEvent };
